import React from 'react';
import '../../Website.css';

const TermsOfService = () => {
  return (
    <div className="tw-bg-gray-2 min-h-screen">
      <style>{`
        html {
          scroll-behavior: smooth;
        }
      `}</style>
      <main className="tw-w-full tw-max-w-[1682px] tw-mx-auto tw-px-4 sm:tw-px-6 lg:tw-px-14 tw-py-16 sm:tw-py-20">
        <div className="tw-bg-white tw-p-8 tw-rounded-lg tw-shadow-lg tw-text-gray-800">
          <h2 className="tw-text-blue-3 tw-text-4xl sm:tw-text-5xl md:tw-text-6xl tw-font-semibold tw-text-center tw-mb-12">
            Terms of Service
          </h2>
          <p className="tw-text-sm tw-text-gray-600 tw-text-center">Effective: August 25, 2025</p>
          <p className="tw-text-sm tw-text-gray-600 tw-text-center tw-mb-8">Last Updated: August 25, 2025</p>

          <div className="tw-mb-12 tw-p-6 tw-bg-gray-50 tw-rounded-lg">
            <h3 className="tw-text-xl tw-font-semibold tw-mb-4">Table of Contents</h3>
            <ol className="list-decimal tw-list-inside tw-space-y-2 tw-text-blue-700">
              <li>
                <a href="#service-description" className="hover:tw-underline">
                  Service Description
                </a>
              </li>
              <li>
                <a href="#account-types" className="hover:tw-underline">
                  Account Types & Eligibility
                </a>
              </li>
              <li>
                <a href="#user-management" className="hover:tw-underline">
                  User Management & Licensing (B2B)
                </a>
              </li>
              <li>
                <a href="#ai-services" className="hover:tw-underline">
                  AI Services & Content
                </a>
              </li>
              <li>
                <a href="#user-content" className="hover:tw-underline">
                  User Content & IP
                </a>
              </li>
              <li>
                <a href="#data-privacy" className="hover:tw-underline">
                  Data & Privacy
                </a>
              </li>
              <li>
                <a href="#payments" className="hover:tw-underline">
                  Payments & Billing
                </a>
              </li>
              <li>
                <a href="#service-level" className="hover:tw-underline">
                  Service Level Agreement (B2B Only)
                </a>
              </li>
              <li>
                <a href="#prohibited-activities" className="hover:tw-underline">
                  Prohibited Activities
                </a>
              </li>
              <li>
                <a href="#job-applications" className="hover:tw-underline">
                  Job Applications
                </a>
              </li>
              <li>
                <a href="#security-compliance" className="hover:tw-underline">
                  Security & Compliance
                </a>
              </li>
              <li>
                <a href="#disclaimers-limitations" className="hover:tw-underline">
                  Disclaimers & Limitations
                </a>
              </li>
              <li>
                <a href="#indemnification" className="hover:tw-underline">
                  Indemnification
                </a>
              </li>
              <li>
                <a href="#termination" className="hover:tw-underline">
                  Termination
                </a>
              </li>
              <li>
                <a href="#legal-terms" className="hover:tw-underline">
                  Legal Terms
                </a>
              </li>
              <li>
                <a href="#academic-calendar" className="hover:tw-underline">
                  Academic Calendar Considerations (B2B Educational)
                </a>
              </li>
              <li>
                <a href="#contact" className="hover:tw-underline">
                  Contact
                </a>
              </li>
            </ol>
          </div>

          <p className="tw-mb-4">By using SeekJobs.AI, you agree to these Terms. If you disagree, discontinue use immediately.</p>

          <h2 id="service-description" className="tw-text-2xl tw-font-semibold tw-mb-4 tw-mt-6">
            1. Service Description
          </h2>
          <p className="tw-mb-4">
            SeekJobs.AI provides AI-powered career services including resume generation, job matching, and skills analysis.
          </p>
          <p className="tw-mb-4">We redirect users to employer websites for applications—we do not submit applications on your behalf.</p>

          <h2 id="account-types" className="tw-text-2xl tw-font-semibold tw-mb-4 tw-mt-6">
            2. Account Types & Eligibility
          </h2>
          <h3 className="tw-text-xl tw-font-semibold tw-mb-2">Individual Users (B2C)</h3>
          <ul className="list-disc tw-list-inside tw-mb-4 tw-pl-4">
            <li>Must be 16+ years old (16-18 requires parental consent)</li>
            <li>One account per user</li>
            <li>Accurate information required</li>
            <li>Responsible for account security</li>
          </ul>
          <h3 className="tw-text-xl tw-font-semibold tw-mb-2 tw-mt-4">Enterprise/Educational Institution Users (B2B)</h3>
          <ul className="list-disc tw-list-inside tw-mb-4 tw-pl-4">
            <li>Requires executed enterprise agreement</li>
            <li>Administrator must be authorized institutional representative</li>
            <li>Institution responsible for user management and compliance</li>
            <li>FERPA compliance required for educational institutions</li>
            <li>Minimum user seat commitments apply</li>
          </ul>

          <h2 id="user-management" className="tw-text-2xl tw-font-semibold tw-mb-4 tw-mt-6">
            3. User Management & Licensing (B2B)
          </h2>
          <h3 className="tw-text-xl tw-font-semibold tw-mb-2">License Allocation</h3>
          <ul className="list-disc tw-list-inside tw-mb-4 tw-pl-4">
            <li>Licenses are non-transferable between institutions</li>
            <li>Overage fees apply for exceeding licensed seats</li>
            <li>Administrator controls user provisioning/de-provisioning</li>
            <li>SSO integration available per technical specifications</li>
          </ul>
          <h3 className="tw-text-xl tw-font-semibold tw-mb-2 tw-mt-4">Data Ownership</h3>
          <ul className="list-disc tw-list-inside tw-mb-4 tw-pl-4">
            <li>Institution retains rights to aggregated institutional data</li>
            <li>Individual users retain rights to personal profile data</li>
            <li>Student data subject to FERPA and institutional privacy policies</li>
            <li>Bulk data export available upon contract termination</li>
          </ul>

          <h2 id="ai-services" className="tw-text-2xl tw-font-semibold tw-mb-4 tw-mt-6">
            4. AI Services & Content
          </h2>
          <p className="tw-mb-4">
            <strong>AI Disclaimer:</strong> AI-generated content may contain errors. You are solely responsible for reviewing and approving
            all content before use.
          </p>
          <p className="tw-mb-4">We disclaim liability for AI-generated materials.</p>
          <p className="tw-mb-4">
            <strong>Your Responsibility:</strong> Verify all AI suggestions, resumes, and cover letters before submission to employers.
          </p>

          <h2 id="user-content" className="tw-text-2xl tw-font-semibold tw-mb-4 tw-mt-6">
            5. User Content & IP
          </h2>
          <p className="tw-mb-4">You retain ownership of uploaded content</p>
          <p className="tw-mb-4">You grant us license to process content for service delivery and AI training (anonymized)</p>
          <p className="tw-mb-4">All content must be accurate, lawful, and professional</p>
          <p className="tw-mb-4">We own our platform, algorithms, and technology</p>

          <h2 id="data-privacy" className="tw-text-2xl tw-font-semibold tw-mb-4 tw-mt-6">
            6. Data & Privacy
          </h2>
          <p className="tw-mb-4">Governed by our Privacy Policy. Enterprise clients receive additional data processing agreements.</p>
          <p className="tw-mb-4">
            Educational institutions must ensure proper consent for student data processing through AI providers (OpenAI, Google Gemini,
            Amazon Nova Pro).
          </p>

          <h2 id="payments" className="tw-text-2xl tw-font-semibold tw-mb-4 tw-mt-6">
            7. Payments & Billing
          </h2>
          <h3 className="tw-text-xl tw-font-semibold tw-mb-2">Individual Users (B2C)</h3>
          <ul className="list-disc tw-list-inside tw-mb-4 tw-pl-4">
            <li>Free and Premium tiers available</li>
            <li>Fees are non-refundable except as required by law</li>
            <li>No refunds policy applies to all purchases</li>
            <li>Credits expire 12 months after purchase</li>
          </ul>
          <h3 className="tw-text-xl tw-font-semibold tw-mb-2 tw-mt-4">Enterprise/Educational Users (B2B)</h3>
          <ul className="list-disc tw-list-inside tw-mb-4 tw-pl-4">
            <li>Annual licensing with optional multi-year terms</li>
            <li>Payment terms: NET 30 days from invoice date</li>
            <li>Purchase orders accepted for institutional clients</li>
            <li>Pricing based on licensed seats with volume discounts</li>
            <li>No refunds except for service failures exceeding SLA remedies</li>
            <li>Auto-renewal with 60-day termination notice</li>
            <li>True-up billing for seat overages</li>
          </ul>

          <h2 id="service-level" className="tw-text-2xl tw-font-semibold tw-mb-4 tw-mt-6">
            8. Service Level Agreement (B2B Only)
          </h2>
          <ul className="list-disc tw-list-inside tw-mb-4 tw-pl-4">
            <li>Uptime: 99.5% availability (excludes scheduled maintenance)</li>
            <li>Support: Business hours response within 4 hours (critical), 24 hours (standard)</li>
            <li>Maintenance: Scheduled during non-peak hours with 48-hour notice</li>
            <li>Remedies: Service credits for SLA failures (not cash refunds)</li>
          </ul>

          <h2 id="prohibited-activities" className="tw-text-2xl tw-font-semibold tw-mb-4 tw-mt-6">
            9. Prohibited Activities
          </h2>
          <p className="tw-mb-4">You may not:</p>
          <ul className="list-disc tw-list-inside tw-mb-4 tw-pl-4">
            <li>Provide false information or impersonate others</li>
            <li>Use automated systems to access our platform without permission</li>
            <li>Violate laws, harass users, or abuse our services</li>
            <li>Reverse engineer or copy our technology</li>
            <li>Exceed licensed seat allocations (B2B)</li>
            <li>Share login credentials between users</li>
          </ul>

          <h2 id="job-applications" className="tw-text-2xl tw-font-semibold tw-mb-4 tw-mt-6">
            10. Job Applications
          </h2>
          <p className="tw-mb-4">We redirect you to employer websites—you apply directly</p>
          <p className="tw-mb-4">No guarantees of job placement or employer response</p>
          <p className="tw-mb-4">You are responsible for evaluating all opportunities</p>
          <p className="tw-mb-4">We are not party to any employment relationships</p>

          <h2 id="security-compliance" className="tw-text-2xl tw-font-semibold tw-mb-4 tw-mt-6">
            11. Security & Compliance
          </h2>
          <h3 className="tw-text-xl tw-font-semibold tw-mb-2">All Users</h3>
          <ul className="list-disc tw-list-inside tw-mb-4 tw-pl-4">
            <li>Data encryption in transit and at rest</li>
            <li>Regular security assessments</li>
            <li>Incident response procedures</li>
          </ul>
          <h3 className="tw-text-xl tw-font-semibold tw-mb-2 tw-mt-4">Enterprise Users (B2B)</h3>
          <ul className="list-disc tw-list-inside tw-mb-4 tw-pl-4">
            <li>SOC 2 Type II compliance available</li>
            <li>Security questionnaire completion</li>
            <li>Breach notification within 24 hours</li>
            <li>Optional penetration testing coordination</li>
          </ul>

          <h2 id="disclaimers-limitations" className="tw-text-2xl tw-font-semibold tw-mb-4 tw-mt-6">
            12. Disclaimers & Limitations
          </h2>
          <h3 className="tw-text-xl tw-font-semibold tw-mb-2">SERVICE PROVIDED "AS IS"</h3>
          <h3 className="tw-text-xl tw-font-semibold tw-mb-2 tw-mt-4">Individual Users (B2C)</h3>
          <ul className="list-disc tw-list-inside tw-mb-4 tw-pl-4">
            <li>
              <strong>NO WARRANTIES:</strong> We disclaim all warranties, express or implied.
            </li>
            <li>
              <strong>LIMITATION OF LIABILITY:</strong> Our total liability is limited to the lesser of (a) amounts paid in the preceding 12
              months or (b) $100.
            </li>
          </ul>
          <h3 className="tw-text-xl tw-font-semibold tw-mb-2 tw-mt-4">Enterprise Users (B2B)</h3>
          <ul className="list-disc tw-list-inside tw-mb-4 tw-pl-4">
            <li>
              <strong>LIMITED WARRANTIES:</strong> Standard commercial software warranties apply.
            </li>
            <li>
              <strong>LIMITATION OF LIABILITY:</strong> Our total liability is limited to amounts paid in the preceding 12 months, with
              minimum cap of $10,000 for institutional clients.
            </li>
            <li>
              <strong>EXCLUDED DAMAGES:</strong> Neither party liable for indirect, consequential, or punitive damages.
            </li>
          </ul>

          <h2 id="indemnification" className="tw-text-2xl tw-font-semibold tw-mb-4 tw-mt-6">
            13. Indemnification
          </h2>
          <h3 className="tw-text-xl tw-font-semibold tw-mb-2">Mutual Indemnification (B2B)</h3>
          <p className="tw-mb-4">
            Each party agrees to defend the other against third-party claims arising from their breach of these terms or negligent acts.
          </p>
          <h3 className="tw-text-xl tw-font-semibold tw-mb-2 tw-mt-4">User Indemnification (B2C)</h3>
          <p className="tw-mb-4">
            You agree to defend and hold harmless SeekJobs.AI from claims arising from your use of the service or violations of these terms.
          </p>

          <h2 id="termination" className="tw-text-2xl tw-font-semibold tw-mb-4 tw-mt-6">
            14. Termination
          </h2>
          <h3 className="tw-text-xl tw-font-semibold tw-mb-2">Individual Users (B2C)</h3>
          <ul className="list-disc tw-list-inside tw-mb-4 tw-pl-4">
            <li>Terminate anytime</li>
            <li>We may terminate for violations or at our discretion</li>
            <li>Data deleted per retention policy</li>
          </ul>
          <h3 className="tw-text-xl tw-font-semibold tw-mb-2 tw-mt-4">Enterprise Users (B2B)</h3>
          <ul className="list-disc tw-list-inside tw-mb-4 tw-pl-4">
            <li>Either party may terminate with 60-day written notice</li>
            <li>Immediate termination for material breach (30-day cure period)</li>
            <li>Data extraction period: 90 days post-termination</li>
            <li>Prepaid fees non-refundable except for our material breach</li>
          </ul>

          <h2 id="legal-terms" className="tw-text-2xl tw-font-semibold tw-mb-4 tw-mt-6">
            15. Legal Terms
          </h2>
          <ul className="list-disc tw-list-inside tw-mb-4 tw-pl-4">
            <li>Governing Law: Michigan, USA</li>
            <li>Jurisdiction: Michigan courts for B2B; arbitration for B2C</li>
            <li>Arbitration (B2C): AAA arbitration in Michigan</li>
            <li>Class Action Waiver (B2C): No class actions permitted</li>
            <li>Contract Terms (B2B): Governed by executed enterprise agreement</li>
            <li>Modification: Terms may change with 30-day notice</li>
            <li>Force Majeure: Not liable for events beyond reasonable control</li>
          </ul>

          <h2 id="academic-calendar" className="tw-text-2xl tw-font-semibold tw-mb-4 tw-mt-6">
            16. Academic Calendar Considerations (B2B Educational)
          </h2>
          <ul className="list-disc tw-list-inside tw-mb-4 tw-pl-4">
            <li>Billing cycles align with academic years when requested</li>
            <li>Summer suspension options for K-12 institutions</li>
            <li>Graduated implementation for large deployments</li>
          </ul>

          <h2 id="contact" className="tw-text-2xl tw-font-semibold tw-mb-4 tw-mt-6">
            17. Contact
          </h2>
          <ul className="list-disc tw-list-inside tw-mb-4 tw-pl-4">
            <li>Individual & Enterprise Support: support@seekjobs.ai</li>
            <li>Legal/Compliance: legal@seekjobs.ai</li>
            <li>Address: 26700 Lahser Rd, Suite 350, Southfield, MI 48033</li>
            <li>Website: https://seekjobs.ai</li>
          </ul>

          <p className="tw-mt-8 tw-text-sm tw-text-gray-600">
            <strong>IMPORTANT:</strong> By using SeekJobs.AI, you acknowledge reading and agreeing to these Terms.
          </p>
          <p className="tw-mb-4 tw-text-sm tw-text-gray-600">
            B2C terms limit our liability and require arbitration. B2B terms subject to separate enterprise agreements.
          </p>
        </div>
      </main>
    </div>
  );
};

export default TermsOfService;
